(() => {
  const STORAGE_KEY = 'sunbow:attribution:v1';
  const COOKIE_KEY = 'sunbow_attribution_v1';
  const SESSION_ENTRY_KEY = 'sunbow:attribution-session-entry';
  const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
  const RAW_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'gclid',
    'gbraid',
    'wbraid',
    'msclkid',
  ];

  let memoryRecord = null;

  const clean = (value, maxLength = 500) => String(value || '').trim().slice(0, maxLength);
  const pagePath = () => clean(`${window.location.pathname}${window.location.hash || ''}`, 500) || '/';
  const normalizedDomain = (value) => clean(value, 255).toLowerCase().replace(/^www\./, '');

  const parseJson = (value) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const readCookie = () => {
    try {
      const prefix = `${COOKIE_KEY}=`;
      const match = document.cookie.split('; ').find((item) => item.startsWith(prefix));
      return match ? parseJson(decodeURIComponent(match.slice(prefix.length))) : null;
    } catch (error) {
      return null;
    }
  };

  const readRecord = () => {
    let stored = null;
    try {
      stored = parseJson(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {}

    const record = stored || readCookie() || memoryRecord;
    if (!record || Number(record.expires_at || 0) < Date.now()) return null;
    return record;
  };

  const saveRecord = (record) => {
    memoryRecord = record;
    const serialized = JSON.stringify(record);

    try {
      window.localStorage.setItem(STORAGE_KEY, serialized);
    } catch (error) {}

    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${COOKIE_KEY}=${encodeURIComponent(serialized)}; Max-Age=${Math.floor(MAX_AGE_MS / 1000)}; Path=/; SameSite=Lax${secure}`;
    } catch (error) {}
  };

  const rawParameters = () => {
    const params = new URLSearchParams(window.location.search);
    return RAW_KEYS.reduce((values, key) => {
      values[key] = clean(params.get(key), 350);
      return values;
    }, {});
  };

  const externalReferrer = () => {
    const value = clean(document.referrer, 1000);
    if (!value) return { url: '', domain: '' };

    try {
      const referrer = new URL(value);
      const currentDomain = normalizedDomain(window.location.hostname);
      const referrerDomain = normalizedDomain(referrer.hostname);
      if (referrerDomain === currentDomain) return { url: '', domain: '' };
      return { url: clean(referrer.href, 1000), domain: referrerDomain };
    } catch (error) {
      return { url: '', domain: '' };
    }
  };

  const isPaidMedium = (medium) => /(^|_)(paid|cpc|ppc|cpm|display|sem)(_|$)/.test(medium) || medium.includes('paid');
  const isMetaSource = (source) => /(^|[^a-z])(fb|facebook|meta|instagram|ig)([^a-z]|$)/.test(source);
  const isGoogleSource = (source) => source === 'google' || source.startsWith('google_');

  const classify = (raw, referrerDomain) => {
    const source = clean(raw.utm_source).toLowerCase();
    const medium = clean(raw.utm_medium).toLowerCase();
    const domain = normalizedDomain(referrerDomain);

    if (raw.gclid || raw.gbraid || raw.wbraid || (isGoogleSource(source) && isPaidMedium(medium))) return 'Google Ads';
    if (raw.msclkid || ((source === 'bing' || source === 'microsoft') && isPaidMedium(medium))) return 'Microsoft Ads';
    if (isMetaSource(source) && isPaidMedium(medium)) return 'Meta Ads';
    if (source.includes('craigslist') || domain.includes('craigslist.')) return 'Craigslist';
    if (source.includes('instagram') || source === 'ig' || domain.includes('instagram.com')) return 'Instagram Referral/Organic';
    if (isMetaSource(source) || raw.fbclid || domain.includes('facebook.com')) return 'Facebook Referral/Organic';
    if ((isGoogleSource(source) && medium.includes('organic')) || /(^|\.)google\./.test(domain)) return 'Google Organic';
    if ((source === 'bing' && medium.includes('organic')) || domain === 'bing.com' || domain.endsWith('.bing.com')) return 'Bing Organic';
    if (source || medium || raw.utm_campaign || raw.utm_content || raw.utm_term) return 'Other Campaign';
    if (domain) return 'Other Referral';
    return 'Direct / Unknown';
  };

  const makeTouch = () => {
    const raw = rawParameters();
    const referrer = externalReferrer();
    const hasRawAttribution = RAW_KEYS.some((key) => Boolean(raw[key]));

    return {
      source: classify(raw, referrer.domain),
      timestamp: new Date().toISOString(),
      landing_page: pagePath(),
      referring_url: referrer.url,
      referring_domain: referrer.domain,
      raw,
      identifiable: hasRawAttribution || Boolean(referrer.domain),
    };
  };

  const capture = () => {
    const touch = makeTouch();
    const existing = readRecord();
    const record = existing || {
      version: 1,
      first_touch: touch,
      last_touch: touch,
      latest_entry_page: touch.landing_page,
    };

    if (existing && touch.identifiable) {
      record.last_touch = touch;
      record.latest_entry_page = touch.landing_page;
    }

    try {
      if (!window.sessionStorage.getItem(SESSION_ENTRY_KEY)) {
        record.latest_entry_page = touch.landing_page;
        window.sessionStorage.setItem(SESSION_ENTRY_KEY, touch.timestamp);
      }
    } catch (error) {
      if (!record.latest_entry_page) record.latest_entry_page = touch.landing_page;
    }

    record.expires_at = Date.now() + MAX_AGE_MS;
    saveRecord(record);
    return record;
  };

  const bestRawValue = (record, key) => record?.last_touch?.raw?.[key] || record?.first_touch?.raw?.[key] || '';

  const fieldValues = (record = capture()) => {
    const first = record.first_touch || {};
    const last = record.last_touch || first;
    const values = {
      lead_source: last.source || first.source || 'Direct / Unknown',
      first_touch_source: first.source || 'Direct / Unknown',
      last_touch_source: last.source || first.source || 'Direct / Unknown',
      first_landing_page: first.landing_page || '',
      latest_entry_page: record.latest_entry_page || first.landing_page || '',
      first_referring_url: first.referring_url || '',
      first_referring_domain: first.referring_domain || '',
      last_referring_url: last.referring_url || '',
      last_referring_domain: last.referring_domain || '',
      first_touch_timestamp: first.timestamp || '',
      last_touch_timestamp: last.timestamp || first.timestamp || '',
    };

    RAW_KEYS.forEach((key) => {
      values[key] = bestRawValue(record, key);
      values[`first_${key}`] = first.raw?.[key] || '';
      values[`last_${key}`] = last.raw?.[key] || '';
    });

    return values;
  };

  const populate = (root = document, record = capture()) => {
    const values = fieldValues(record);
    root.querySelectorAll('[data-attribution-field]').forEach((field) => {
      field.value = values[field.dataset.attributionField] || '';
    });
    return values;
  };

  const getLeadPayload = (record = readRecord() || capture()) => {
    const values = fieldValues(record);
    return {
      lead_source: values.lead_source,
      first_touch_source: values.first_touch_source,
      last_touch_source: values.last_touch_source,
      first_landing_page: values.first_landing_page,
      latest_entry_page: values.latest_entry_page,
      first_referring_domain: values.first_referring_domain,
      last_referring_domain: values.last_referring_domain,
      utm_source: values.utm_source,
      utm_medium: values.utm_medium,
      utm_campaign: values.utm_campaign,
    };
  };

  window.SunbowAttribution = { capture, classify, fieldValues, getLeadPayload, populate };
})();
